import React from 'react';
import { Check, Zap, CreditCard, Calendar, Mail, Database, Code, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Navbar from './Navbar';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';

const IntegrationsPage = () => {
  const { login } = useAuth();

  const mainIntegrations = [
    {
      name: 'Stripe',
      icon: CreditCard,
      color: 'bg-purple-500',
      description: 'Processe pagamentos diretamente no chat. Aceite cartões de crédito e PIX.',
      features: ['Pagamentos recorrentes', 'Links de pagamento', 'Reembolsos automáticos']
    },
    {
      name: 'Zapier',
      icon: Zap,
      color: 'bg-orange-500',
      description: 'Conecte com mais de 5.000 aplicativos. Automatize qualquer fluxo de trabalho.',
      features: ['5.000+ apps', 'Triggers automáticos', 'Fluxos personalizados']
    },
    {
      name: 'Calendly',
      icon: Calendar,
      color: 'bg-blue-500',
      description: 'Agende reuniões automaticamente. Sincronize com Google Calendar.',
      features: ['Agendamento automático', 'Lembretes por WhatsApp', 'Confirmações']
    },
    {
      name: 'Google Sheets',
      icon: Database,
      color: 'bg-green-500',
      description: 'Exporte leads e dados para planilhas automaticamente.',
      features: ['Exportação automática', 'Sincronização em tempo real', 'Relatórios']
    },
  ];

  const additionalIntegrations = [
    { name: 'HubSpot', category: 'CRM', price: 'R$500' },
    { name: 'Shopify', category: 'E-commerce', price: 'R$750' },
    { name: 'OpenAI/ChatGPT', category: 'IA', price: 'Incluído' },
    { name: 'Google Calendar', category: 'Produtividade', price: 'R$300' },
    { name: 'Slack', category: 'Comunicação', price: 'R$400' },
    { name: 'Mailchimp', category: 'Email', price: 'R$400' },
    { name: 'Twilio', category: 'SMS', price: 'R$500' },
    { name: 'Webhook/API', category: 'Desenvolvimento', price: 'R$500+' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-28 pb-16">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#dbeafe] text-[#3b82f6] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="h-4 w-4" />
            Mais de 50 integrações disponíveis
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Conecte suas ferramentas favoritas
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Integre ChatAlfa com CRMs, processadores de pagamento, ferramentas de agendamento 
            e muito mais. Automatize completamente seu negócio.
          </p>
          <Button 
            onClick={login}
            size="lg" 
            className="bg-[#3b82f6] hover:bg-[#2563eb] text-lg px-8"
            data-testid="integrations-cta"
          >
            Começar gratuitamente
          </Button>
        </div>

        {/* Main Integrations */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Integrações Populares</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {mainIntegrations.map((integration) => (
              <Card key={integration.name} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 ${integration.color} rounded-xl flex items-center justify-center`}>
                      <integration.icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{integration.name}</CardTitle>
                      <p className="text-sm text-gray-500">Integração nativa</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{integration.description}</p>
                  <ul className="space-y-2">
                    {integration.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* How it Works */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Como funcionam as integrações
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Conecte</h3>
                <p className="text-gray-600">
                  Escolha a integração desejada e conecte sua conta com apenas alguns cliques.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Configure</h3>
                <p className="text-gray-600">
                  Defina os gatilhos e ações que deseja automatizar no seu chatbot.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Automatize</h3>
                <p className="text-gray-600">
                  Deixe a automação trabalhar por você 24/7, economizando tempo e recursos.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* All Integrations */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
              Todas as Integrações
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Conecte com qualquer ferramenta que você já usa
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {additionalIntegrations.map((item) => (
                <Card key={item.name} className="hover:border-[#3b82f6] transition-colors cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.category}</p>
                      </div>
                      <span className="text-sm font-medium text-[#3b82f6]">{item.price}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* API Section */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Code className="h-4 w-4" />
                  Para Desenvolvedores
                </div>
                <h2 className="text-3xl font-bold mb-4">API JSON completa</h2>
                <p className="text-gray-300 mb-6">
                  Construa integrações personalizadas com nossa API RESTful. 
                  Documentação completa e suporte técnico incluídos.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-400" />
                    <span>Webhooks em tempo real</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-400" />
                    <span>Autenticação OAuth 2.0</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-400" />
                    <span>Rate limits generosos</span>
                  </li>
                </ul>
                <Button variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100">
                  Ver Documentação
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
              <div className="bg-gray-800 rounded-xl p-6 font-mono text-sm">
                <div className="text-gray-400 mb-2">// Exemplo de requisição</div>
                <div className="text-green-400">POST /api/messages</div>
                <div className="text-gray-300 mt-4">
                  <span className="text-purple-400">{"{"}</span><br/>
                  <span className="ml-4">"to": <span className="text-yellow-400">"5511999999999"</span>,</span><br/>
                  <span className="ml-4">"message": <span className="text-yellow-400">"Olá!"</span>,</span><br/>
                  <span className="ml-4">"bot_id": <span className="text-yellow-400">"bot_abc123"</span></span><br/>
                  <span className="text-purple-400">{"}"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Integration */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] text-white">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Precisa de uma integração personalizada?</h2>
                <p className="text-blue-100 mb-6">
                  Nossa equipe pode desenvolver integrações customizadas para o seu negócio.
                </p>
                <Button 
                  onClick={login}
                  variant="secondary" 
                  size="lg" 
                  className="bg-white text-[#3b82f6] hover:bg-gray-100"
                >
                  Falar com especialista
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IntegrationsPage;
